import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Layout, Maximize2, AlignLeft, Check } from 'lucide-react';

export default function LayoutComposition() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Layout & Composition
        </h1>
        <p className="text-base text-body leading-relaxed">
          Our layouts balance technical precision with modern design principles for optimal readability and impact.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-2xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <Layout className="h-6 w-6" />
          Grid System
        </h2>
        <p className="text-body mb-6">
          Use a 12-column grid system with consistent gutters for all layouts.
        </p>
        <div className="bg-background p-6 rounded-lg">
          <div className="grid grid-cols-12 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="h-16 bg-jackstone-blue/20 rounded flex items-center justify-center text-xs text-subtle">
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Spacing Guidelines
        </h2>
        <Card className="p-6">
          <p className="text-body mb-4">
            Use a consistent 8px spacing scale for all layout elements:
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">4px</div>
              <div className="h-4 w-4 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Tight spacing (icons, small elements)</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">8px</div>
              <div className="h-4 w-8 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Base spacing unit</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">16px</div>
              <div className="h-4 w-16 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Standard padding/margins</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">24px</div>
              <div className="h-4 w-24 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Component spacing</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">32px</div>
              <div className="h-4 w-32 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Section spacing (mobile)</div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-16 text-sm font-mono text-subtle">48px</div>
              <div className="h-4 w-48 bg-jackstone-blue rounded"></div>
              <div className="text-sm text-subtle">Section spacing (desktop)</div>
            </div>
          </div>
        </Card>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <Maximize2 className="h-6 w-6" />
          Content Width & Margins
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <Badge className="mb-3">Maximum Content Width</Badge>
            <p className="text-body mb-4">
              Limit text content to 1200px maximum width for optimal readability.
              Full-width images and backgrounds are acceptable.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-subtle">Mobile</span>
                <span className="font-mono">100% - 32px margins</span>
              </div>
              <div className="flex justify-between">
                <span className="text-subtle">Tablet</span>
                <span className="font-mono">100% - 48px margins</span>
              </div>
              <div className="flex justify-between">
                <span className="text-subtle">Desktop</span>
                <span className="font-mono">1200px max-width</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Responsive Breakpoints</Badge>
            <p className="text-body mb-4">
              Use these breakpoints for responsive design consistency:
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-subtle">Mobile</span>
                <span className="font-mono">0 - 767px</span>
              </div>
              <div className="flex justify-between">
                <span className="text-subtle">Tablet</span>
                <span className="font-mono">768px - 1023px</span>
              </div>
              <div className="flex justify-between">
                <span className="text-subtle">Desktop</span>
                <span className="font-mono">1024px+</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-4 flex items-center gap-2">
          <AlignLeft className="h-5 w-5" />
          Alignment Principles
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-body mb-2">Left Alignment</h4>
            <p className="text-sm text-subtle">
              Default alignment for all body text and most content for natural reading flow.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Center Alignment</h4>
            <p className="text-sm text-subtle">
              Reserve for hero sections, callouts, and short headlines only.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Grid Alignment</h4>
            <p className="text-sm text-subtle">
              All elements should align to the grid system for visual consistency.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Whitespace Philosophy
        </h3>
        <p className="text-body">
          Generous whitespace is essential to our brand identity. It creates a professional,
          premium feel and improves content comprehension. Don't be afraid of empty space—
          it gives our technical content room to breathe and enhances the industrial precision
          of our visual language.
        </p>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Layout Best Practices
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Use consistent card components for grouping related information
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Establish clear visual hierarchy through size, weight, and spacing
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Maintain consistent margins and padding throughout the design
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Use dividers and section breaks to organize complex information
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
